import React, { useState, useEffect, useRef } from 'react'
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react'

// ============================================================
// BUTTON
// ============================================================
export function Button({
  children, variant = 'primary', size = 'md', fullWidth = false,
  loading = false, icon, iconRight, onClick, type = 'button',
  disabled = false, className = '', ...props
}) {
  const cls = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : size === 'xl' ? 'btn-xl' : '',
    size === 'icon' ? 'btn-icon' : '',
    fullWidth ? 'btn-full' : '',
    className
  ].filter(Boolean).join(' ')

  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled || loading} {...props}>
      {loading && <span className="animate-spin" style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%' }} />}
      {!loading && icon && icon}
      {children}
      {!loading && iconRight && iconRight}
    </button>
  )
}

// ============================================================
// INPUT
// ============================================================
export function Input({
  label, error, hint, required, icon, iconRight, className = '', ...props
}) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && <span className="required">*</span>}</label>}
      <div style={{ position: 'relative' }}>
        {icon && (
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', display: 'flex' }}>
            {icon}
          </span>
        )}
        <input
          className={`form-input ${icon ? '' : ''} ${className}`}
          style={{ paddingLeft: icon ? 40 : undefined, paddingRight: iconRight ? 40 : undefined }}
          {...props}
        />
        {iconRight && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', display: 'flex' }}>
            {iconRight}
          </span>
        )}
      </div>
      {error && <span className="form-error"><AlertCircle size={12} />{error}</span>}
      {hint && !error && <span className="form-hint">{hint}</span>}
    </div>
  )
}

// ============================================================
// SELECT
// ============================================================
export function Select({ label, error, hint, required, children, className = '', ...props }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && <span className="required">*</span>}</label>}
      <select className={`form-input form-select ${className}`} {...props}>
        {children}
      </select>
      {error && <span className="form-error"><AlertCircle size={12} />{error}</span>}
      {hint && !error && <span className="form-hint">{hint}</span>}
    </div>
  )
}

// ============================================================
// TEXTAREA
// ============================================================
export function Textarea({ label, error, hint, required, className = '', ...props }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && <span className="required">*</span>}</label>}
      <textarea className={`form-input form-textarea ${className}`} {...props} />
      {error && <span className="form-error"><AlertCircle size={12} />{error}</span>}
      {hint && !error && <span className="form-hint">{hint}</span>}
    </div>
  )
}

// ============================================================
// BADGE
// ============================================================
export function Badge({ children, variant = 'neutral', icon, className = '' }) {
  return (
    <span className={`badge badge-${variant} ${className}`}>
      {icon && icon}
      {children}
    </span>
  )
}

// Status badge helper
export function StatusBadge({ status }) {
  const map = {
    inquiry: { label: 'Inquiry', variant: 'info' },
    approved: { label: 'Approved', variant: 'success' },
    rejected: { label: 'Rejected', variant: 'danger' },
    artwork_pending: { label: 'Artwork Pending', variant: 'warning' },
    artwork_approved: { label: 'Artwork Approved', variant: 'success' },
    artwork_rejected: { label: 'Artwork Rejected', variant: 'danger' },
    active: { label: 'Active', variant: 'success' },
    completed: { label: 'Completed', variant: 'neutral' },
    cancelled: { label: 'Cancelled', variant: 'danger' },
    available: { label: 'Available', variant: 'success' },
    booked: { label: 'Booked', variant: 'warning' },
  }
  const { label, variant } = map[status] || { label: status, variant: 'neutral' }
  return <Badge variant={variant}>{label}</Badge>
}

// ============================================================
// MODAL
// ============================================================
export function Modal({ isOpen, onClose, title, children, footer, size = 'md' }) {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  if (!isOpen) return null

  const maxWidth = { sm: 420, md: 580, lg: 740, xl: 900 }[size] || 580

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal animate-scaleIn" style={{ maxWidth }}>
        <div className="modal-header">
          <div>
            {typeof title === 'string' ? (
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700 }}>{title}</h3>
            ) : title}
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ flexShrink: 0, marginTop: -4 }}>
            <X size={18} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

// ============================================================
// TOAST
// ============================================================
let toastListeners = []
let toastId = 0

export function toast(message, type = 'default', duration = 4000) {
  const id = ++toastId
  toastListeners.forEach(fn => fn({ id, message, type, duration }))
}
toast.success = (msg) => toast(msg, 'success')
toast.error = (msg) => toast(msg, 'error')
toast.warning = (msg) => toast(msg, 'warning')

export function ToastContainer() {
  const [toasts, setToasts] = useState([])

  useEffect(() => {
    const handler = (t) => {
      setToasts(prev => [...prev, t])
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), t.duration)
    }
    toastListeners.push(handler)
    return () => { toastListeners = toastListeners.filter(fn => fn !== handler) }
  }, [])

  const icons = { success: <CheckCircle size={16} />, error: <AlertCircle size={16} />, warning: <AlertTriangle size={16} /> }

  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast ${t.type !== 'default' ? t.type : ''}`}>
          {icons[t.type]}
          <span>{t.message}</span>
          <button onClick={() => setToasts(p => p.filter(x => x.id !== t.id))} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', opacity: 0.7 }}>
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  )
}

// ============================================================
// CARD
// ============================================================
export function Card({ children, elevated = false, className = '', onClick }) {
  return (
    <div className={`card ${elevated ? 'card-elevated' : ''} ${className}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
      {children}
    </div>
  )
}

// ============================================================
// LOADING SPINNER
// ============================================================
export function Spinner({ size = 24, color = 'var(--tech)' }) {
  return (
    <div style={{
      width: size, height: size, border: `2px solid var(--gray-200)`,
      borderTopColor: color, borderRadius: '50%', animation: 'spin 0.8s linear infinite'
    }} />
  )
}

export function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 800, color: 'var(--navy)', marginBottom: 16 }}>
          Ads<span style={{ color: 'var(--tech)' }}>Locate</span>
        </div>
        <Spinner size={32} />
      </div>
    </div>
  )
}

// ============================================================
// EMPTY STATE
// ============================================================
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state-icon">{icon}</div>}
      <div className="empty-state-title">{title}</div>
      {description && <p className="empty-state-desc">{description}</p>}
      {action}
    </div>
  )
}

// ============================================================
// CONFIRM DIALOG
// ============================================================
export function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</Button>
        </>
      }
    >
      <p style={{ color: 'var(--gray-600)' }}>{message}</p>
    </Modal>
  )
}

// ============================================================
// FILE UPLOAD
// ============================================================
export function FileUpload({ label, accept, multiple, onFiles, hint, preview = false }) {
  const [dragging, setDragging] = useState(false)
  const [previews, setPreviews] = useState([])
  const inputRef = useRef()

  function handleFiles(files) {
    if (!files || files.length === 0) return
    const fileArr = Array.from(files)
    onFiles(fileArr)
    if (preview) {
      const urls = fileArr.map(f => URL.createObjectURL(f))
      setPreviews(urls)
    }
  }

  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div
        onClick={() => inputRef.current.click()}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files) }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        style={{
          border: `2px dashed ${dragging ? 'var(--tech)' : 'var(--gray-200)'}`,
          borderRadius: 'var(--radius)',
          padding: '32px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? 'var(--accent-glow)' : 'var(--gray-50)',
          transition: 'all 0.2s'
        }}
      >
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>📁</div>
        <p style={{ fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>Click to upload or drag & drop</p>
        {hint && <p style={{ fontSize: '0.82rem', color: 'var(--gray-400)' }}>{hint}</p>}
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files)} />
      </div>
      {preview && previews.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {previews.map((url, i) => (
            <img key={i} src={url} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200)' }} />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================
// AVATAR
// ============================================================
export function Avatar({ name, src, size = 36 }) {
  const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'
  if (src) return <img src={src} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
  const colors = ['#2B5DAB', '#3066BE', '#0D1B3E', '#10B981', '#8B5CF6', '#F59E0B']
  const bg = colors[name ? name.charCodeAt(0) % colors.length : 0]
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontWeight: 700, fontSize: size * 0.38,
      fontFamily: 'var(--font-display)', flexShrink: 0
    }}>
      {initials}
    </div>
  )
}
